<template>
    <div>
        <vrm-header></vrm-header>
        <vrm-title>
            <template v-slot:back-link>
                <a href="/groups" class="vrm-back-link">
                    <span class="vrm-back-link__icon sg-vrm-icon_048_arrow-left-16px"></span>
                    {{ $t('general.back', { i18nFallback: 'Back' }) }}
                </a>
            </template>
            <template v-slot:page-title>
                <h1 class="vrm-page-title">
                    <span>
                        {{
                            group ? group.name : $t('core.loading', { i18nFallback: 'Loading...' })
                        }}
                    </span>
                </h1>
            </template>
        </vrm-title>
        <div v-if="group" class="container">
            <div class="sg-spread">
                <span>
                    {{
                        $t('groups.owner_name', { i18nFallback: ' Owner: {name}', name: ownerName })
                    }}
                    <br />
                    <span>
                        {{ $t('teams.admin', { i18nFallback: 'Admin' }) + ': ' }}
                    </span>
                    <!-- Remove canMakeChange-->
                    <span v-if="admins.length === 0">
                        {{ $t('teams.none', { i18nFallback: 'None' }) }}
                    </span>
                    <span v-else>
                        <span
                            v-for="(admin, index) in admins"
                            :key="admin.idUserGroup ? admin.idUserGroup : admin.idUser"
                        >
                            <span>{{ admin.name }}</span>
                            <span v-if="index < admins.length - 1">,&nbsp;</span>
                        </span>
                    </span>
                </span>
                <button
                    v-if="canMakeChanges"
                    type="button"
                    class="sg-btn sg-blue"
                    @click.prevent="editGroup"
                >
                    {{ $t('groups.edit_group', { i18nFallback: 'Edit group' }) }}
                </button>
            </div>
            <h2>
                {{ $t('groups.installations', { i18nFallback: 'Installations' }) }}
            </h2>
            <vrm-scroll-fade>
                <vrm-foldable-table :items="group.sites" key-by="idSite">
                    <template v-slot:thead>
                        <tr>
                            <th>
                                {{ $t('groups.installations', { i18nFallback: 'Installations' }) }}
                            </th>
                            <th />
                        </tr>
                    </template>
                    <template v-slot:main="{ item: site }">
                        <td class="clickable" @click="() => goToInstallation(site.idSite)">
                            <span
                                :class="convertDeviceIcon(site.device_icon)"
                                class="vrm-table__icon"
                            />
                            {{ site.description }}
                        </td>
                        <td class="vrm-table__actions">
                            <a
                                v-if="canUnlinkSite(site.idSite)"
                                class="vrm-table__icon--link"
                                @click.prevent="unlinkSite(site.idSite)"
                            >
                                <i class="sg-vrm-icon_055_cross-16px" />
                            </a>
                        </td>
                    </template>
                    <template v-slot:noEntries>
                        {{
                            $t('users.no_teams', {
                                i18nFallback:
                                    'No teams were found for this installation. You can add a new team by clicking on the "Add Teams" button.',
                            })
                        }}
                    </template>
                </vrm-foldable-table>
            </vrm-scroll-fade>
            <div class="sg-btn__container sg-btn__container--align-right">
                <button
                    v-if="canMakeChanges"
                    class="sg-btn sg-blue"
                    @click.prevent="linkSitesPopup"
                >
                    {{ $t('groups.add_installations', { i18nFallback: 'Add installations' }) }}
                </button>
            </div>
            <h2>
                {{
                    $t('groups.linked_users_and_teams', { i18nFallback: 'Linked users and teams' })
                }}
            </h2>
            <vrm-scroll-fade>
                <vrm-foldable-table :items="combinedUserList" key-by="key">
                    <template v-slot:thead>
                        <tr>
                            <th>
                                {{
                                    $t('groups.users_and_teams', {
                                        i18nFallback: 'Users and teams',
                                    })
                                }}
                            </th>
                            <th>{{ $t('groups.users', { i18nFallback: 'Users' }) }}</th>
                            <th>
                                {{ $t('users.role', { i18nFallback: 'Role' }) }}
                                <span
                                    class="sg-vrm-icon_061_info-round-16px vrm-table__icon--link"
                                    @click="showRoleGuideModal"
                                ></span>
                            </th>
                            <th>
                                {{
                                    $t('groups.alarm_notifications', {
                                        i18nFallback: 'Alarm notifications',
                                    })
                                }}
                            </th>
                            <th colspan="999" />
                        </tr>
                    </template>
                    <template v-slot:main="{ item, toggle, expanded }">
                        <td v-if="itemIsOwner(item)">
                            {{ item.title }}
                            <span>({{ $t('groups.owner', { i18nFallback: ' Owner' }) }})</span>
                        </td>
                        <td v-else>{{ item.title }}</td>
                        <td>{{ item.isGroup ? item.group.userCount : '-' }}</td>
                        <td class="ui-select--simple">
                            <vrm-dropdown
                                v-model="item.accessLevelOption"
                                data-cy="access-level"
                                :options="accessLevelDropdownOptions"
                                :disabled="!canModifyControl(item)"
                                :append-to-body="true"
                                :dropdown-classes="'ui-select--simple'"
                            />
                        </td>
                        <td>
                            <vrm-toggle
                                v-if="!item.isGroup"
                                v-model="item.user.receivesAlarmNotifications"
                                :disabled="!canMakeChanges && !isLoggedInUser(item.user)"
                                :tooltip="
                                    $t('groups.alarm_notifications_subscribe', {
                                        i18nFallback: 'Subscribe to alarm notifications',
                                    })
                                "
                                @input="
                                    alarmsState =>
                                        changeAlarmNotifications({
                                            userId: item.user.idUser,
                                            groupId: item.user.idSiteGroup,
                                            alarmsState,
                                        })
                                "
                            />
                        </td>
                        <td>
                            <div v-if="!item.isGroup">
                                <span
                                    v-if="item.user.intermediateAlarmNotifications"
                                    v-tooltip.bottom="
                                        $t('groups.alarm_notifications_missing_install', {
                                            i18nFallback:
                                                'Some installations have a different alarm notification preference set. You can adjust it in the settings page of the installation, under alarm rules.',
                                        })
                                    "
                                >
                                    <i
                                        class="vrm-table__icon sg-vrm-icon_061_info-round-16px sg-color-orange"
                                    />
                                </span>
                            </div>
                        </td>
                        <td class="vrm-table__actions">
                            <i
                                v-if="canMakeChanges || item.isGroup"
                                class="vrm-table__icon--link"
                                :class="
                                    expanded
                                        ? 'sg-vrm-icon_059_min-round-16px'
                                        : 'sg-vrm-icon_058_plus-round-16px'
                                "
                                @click="toggle"
                            />
                        </td>
                    </template>
                    <template v-slot:extra="{ item }">
                        <td colspan="999">
                            <table v-if="item.isGroup" class="vrm-table">
                                <thead>
                                    <tr>
                                        <th>
                                            {{ $t('groups.users', { i18nFallback: 'Users' }) }}
                                        </th>
                                        <th>
                                            {{ $t('common.email', { i18nFallback: 'Email' }) }}
                                        </th>
                                        <th>
                                            {{
                                                $t('groups.alarm_notifications', {
                                                    i18nFallback: 'Alarm notifications',
                                                })
                                            }}
                                        </th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="user in item.group.users" :key="user.id">
                                        <td class="vrm-table__nowrap">
                                            <vrm-avatar :user="user" />
                                            {{ user.name }}
                                        </td>
                                        <td>
                                            <a :href="'mailto:' + user.email">{{ user.email }}</a>
                                        </td>
                                        <td>
                                            <vrm-toggle
                                                v-model="user.receivesAlarmNotifications"
                                                :disabled="!canMakeChanges && !isLoggedInUser(user)"
                                                :tooltip="
                                                    $t('groups.alarm_notifications_subscribe', {
                                                        i18nFallback:
                                                            'Subscribe to alarm notifications',
                                                    })
                                                "
                                                @input="
                                                    alarmsState =>
                                                        changeAlarmNotifications({
                                                            userId: user.idUser,
                                                            groupId: item.group.idSiteGroup,
                                                            alarmsState,
                                                        })
                                                "
                                            />
                                        </td>
                                        <td class="vrm-table__actions">
                                            <span
                                                v-if="user.intermediateAlarmNotifications"
                                                v-tooltip.bottom="
                                                    $t(
                                                        'groups.alarm_notifications_missing_install',
                                                        {
                                                            i18nFallback:
                                                                'Some installations have a different alarm notification preference set. You can adjust it in the settings page of the installation, under alarm rules.',
                                                        },
                                                    )
                                                "
                                            >
                                                <i
                                                    class="vrm-table__icon sg-vrm-icon_061_info-round-16px sg-color-orange"
                                                />
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div v-if="canMakeChanges" class="sg-spread">
                                <template v-if="item.isGroup">
                                    <button
                                        type="button"
                                        :disabled="busy"
                                        class="sg-btn sg-site"
                                        @click.prevent="unlinkUserGroup(item.group.idUserGroup)"
                                    >
                                        {{
                                            $t('groups.unlink_team', {
                                                i18nFallback: 'Unlink team',
                                            })
                                        }}
                                    </button>
                                    <router-link
                                        type="button"
                                        :to="{
                                            name: 'base.user-group',
                                            params: { id: item.group.idUserGroup },
                                        }"
                                        class="sg-btn sg-blue"
                                    >
                                        {{ $t('groups.view_team', { i18nFallback: 'View team' }) }}
                                    </router-link>
                                </template>
                                <template v-else>
                                    <button
                                        type="button"
                                        :disabled="busy"
                                        class="sg-btn sg-site"
                                        @click.prevent="unlinkUser(item.user.idUser)"
                                    >
                                        {{
                                            $t('groups.unlink_user', {
                                                i18nFallback: 'Unlink user',
                                            })
                                        }}
                                    </button>
                                </template>
                            </div>
                        </td>
                    </template>
                    <template v-slot:noEntries>
                        {{
                            $t('groups.no_members_teams', {
                                i18nFallback:
                                    'No teams or members are linked to this group. You can link a team by clicking on the "Link Teams" button. You can link a user by clicking on the "Link Users" button. ',
                            })
                        }}
                    </template>
                </vrm-foldable-table>
            </vrm-scroll-fade>
            <div v-if="canMakeChanges" class="sg-btn__container sg-btn__container--align-right">
                <button type="button" class="sg-btn sg-site" @click.prevent="linkUserGroupsPopup">
                    {{ $t('groups.link_teams', { i18nFallback: 'Link teams' }) }}
                </button>
                <button type="button" class="sg-btn sg-blue" @click.prevent="linkUsersPopup">
                    {{ $t('groups.link_users', { i18nFallback: 'Link users' }) }}
                </button>
            </div>

            <!-- Invites -->
            <h2>
                {{ $t('users.invitations', { i18nFallback: 'Pending invitations' }) }}
            </h2>
            <vrm-scroll-fade>
                <vrm-foldable-table :items="group.invites" key-by="email">
                    <template v-slot:thead>
                        <tr>
                            <th>
                                {{ $t('users.invited_user', { i18nFallback: 'Invited user' }) }}
                            </th>
                            <th>{{ $t('users.email', { i18nFallback: 'Email' }) }}</th>
                            <th>
                                {{ $t('users.role', { i18nFallback: 'Role' }) }}
                                <span
                                    class="sg-vrm-icon_061_info-round-16px vrm-table__icon--link"
                                    @click="showRoleGuideModal"
                                ></span>
                            </th>
                            <th />
                        </tr>
                    </template>
                    <template v-slot:main="{ item: user }">
                        <td class="vrm-table__nowrap">
                            <vrm-avatar :user="user" />
                            {{ user.name }}
                        </td>
                        <td class="sg-color-blue">
                            <a :href="'mailto:' + user.email">{{ user.email }}</a>
                        </td>
                        <td>
                            {{ findAccessLevelOption(user.accessLevel).name }}
                        </td>
                        <td class="vrm-table__actions">
                            <a
                                v-if="canMakeChanges"
                                class="vrm-table__icon--link sg-vrm-icon_055_cross-16px"
                                @click.prevent="removeInvite(user.email)"
                            />
                        </td>
                    </template>
                    <template v-slot:noEntries>
                        {{
                            $t('groups.no_pending', {
                                i18nFallback:
                                    '"No pending invitations in the Group. You can link a user by clicking on the "Link Users" button.',
                            })
                        }}
                    </template>
                </vrm-foldable-table>
            </vrm-scroll-fade>
            <div v-if="canDelete" class="installation-group-page-delete">
                <h2>
                    {{ $t('groups.delete_group', { i18nFallback: 'Delete group' }) }}
                </h2>
                <p>{{ $t('groups.confirm_deletion_message') }}</p>
                <div class="sg-btn__container sg-btn__container--align-left">
                    <button
                        type="button"
                        class="sg-btn sg-red installation-group-page-del-btn"
                        @click.prevent="onDelete"
                    >
                        {{ $t('groups.delete_group', { i18nFallback: 'Delete group' }) }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { Vue, Component } from 'vue-property-decorator';
import { AngularRoute, ngInject } from '@/vue/decorators/angular-interop';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import VrmHeader from '@/vue/components/layout/VrmHeader';
import VrmTitle from '@/vue/components/layout/VrmTitle';
import VrmIconButton from '@/vue/components/molecules/VrmIconButton';
import InstallationGroupLinkInstallationsModal from '@/vue/components/modals/InstallationGroupLinkInstallationsModal';
import InstallationGroupSettingsModal from '@/vue/components/modals/InstallationGroupSettingsModal';
import VrmFoldableTable from '@/vue/components/molecules/VrmFoldableTable';
import VrmScrollFade from '@/vue/components/shared/VrmScrollFade';
import VrmToggle from '@/vue/components/shared/toggles/VrmToggle';
import VrmDropdown from '@/vue/components/shared/VrmDropdown';
import InstallationGroupLinkUserGroupsModal from '@/vue/components/modals/InstallationGroupLinkUserGroupsModal';
import VrmAvatar from '@/vue/components/shared/VrmAvatar';
import InstallationGroupLinkUsersModal from '@/vue/components/modals/InstallationGroupLinkUsersModal';
import { NOTIFICATION_TYPE_SUCCESS } from '@/vue/store/modules/notifications';
import ConfirmDeletionModal from '@/vue/components/modals/ConfirmDeletionModal';
import UserRolesGuideModal from '@/vue/components/modals/UserRolesGuideModal';
import {
    accessLevelDropdownOptions,
    findAccessLevelOption,
} from '@/vue/store/modules/installation-access-level';

@Component({
    components: {
        VrmAvatar,
        VrmToggle,
        VrmFoldableTable,
        VrmIconButton,
        VrmHeader,
        VrmScrollFade,
        VrmDropdown,
        VrmTitle,
    },
})
@AngularRoute('base.installation-group', '/groups/{id:int}', { slot: 'main' })
@AngularLocaleBundle('core')
@AngularLocaleBundle('nav')
@AngularLocaleBundle('groups')
@AngularLocaleBundle('users')
@AngularLocaleBundle('general')
export default class InstallationGroup extends Vue {
    /** @type InstallationGroupsService */
    @ngInject()
    InstallationGroupsService;
    @ngInject()
    UserService;
    @ngInject()
    $rootScope;
    @ngInject()
    ApiService;
    @ngInject()
    vrmNotificationsService;
    @ngInject()
    VrmModalService;
    @ngInject()
    $filter;

    busy = false;

    accessLevelDropdownOptions = accessLevelDropdownOptions;
    findAccessLevelOption = findAccessLevelOption;

    convertDeviceIcon(iconCode) {
        return this.$filter('vrmDeviceIcon')(iconCode);
    }

    async created() {
        try {
            this.loading = true;
            await this.loadGroups();
            if (!this.group) {
                this.$router.replace('base.installation-groups');
            }
        } finally {
            this.loading = false;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const welcome = urlParams.get('welcome');
        if (welcome) {
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_SUCCESS,
                message: this.$t('groups.welcome', {
                    i18nFallback: 'Welcome to your new group!',
                }),
            });
        }
    }

    async loadGroups() {
        await this.InstallationGroupsService.loadInstallationGroups();
    }

    async reloadGroups() {
        await this.InstallationGroupsService.reloadInstallationGroups();
    }

    get group() {
        return this.$store.state.InstallationGroupsService.groups.find(
            group => group.idSiteGroup == this.$route.params.id,
        );
    }

    get admins() {
        const adminUsers = this.group.users.filter(user => user.isAdmin === 1);
        const adminGroups = this.group.userGroups.filter(userGroup => userGroup.isAdmin === 1);

        return [...adminUsers, ...adminGroups];
    }

    goToInstallation(id) {
        this.$router.push({ name: 'base.installation.dashboard', params: { id } });
    }

    editGroup() {
        if (this.group) {
            this.VrmModalService.openVueModal(InstallationGroupSettingsModal, {
                group: {
                    ...this.group,
                    admins: this.admins,
                },
                idUser: this.localUserId,
            });
        }
    }

    get canDelete() {
        const user = this.$store.state.UserService.user;
        return this.group && user && user.id === this.group.idOwner;
    }

    async onDelete() {
        if (this.busy) return;
        this.busy = true;
        const idGroup = this.group.idSiteGroup;
        const props = {
            item: this.$t('groups.confirm_deletion_item'),
            message: this.$t('groups.confirm_deletion_message'),
        };
        if (await this.VrmModalService.openVueModal(ConfirmDeletionModal, props)) {
            await this.InstallationGroupsService.deleteInstallationGroup(idGroup);
            this.$router.push({ name: 'base.installation-groups' });
        } else {
            this.busy = false;
        }
    }

    async changeAlarmNotifications({ userId, groupId, alarmsState }) {
        const endpoint = `/installations/groups/${groupId}/alarm-user/${userId}`;
        await this.ApiService.call2(
            endpoint,
            { receivesAlarmNotifications: +alarmsState },
            { method: 'PUT' },
        );
        await this.reloadGroups();
    }

    get ownerName() {
        if (!this.group) return '';
        const user = this.group.users.find(u => u.idUser === this.group.idOwner);
        return user ? user.name : '?';
    }

    get localUserId() {
        const user = this.$store.state.UserService.user;
        return user ? user.id : 0;
    }

    get isLoggedInUser() {
        return user => user.idUser === this.localUserId;
    }

    get canMakeChanges() {
        const isOwner = this.group.idOwner === this.localUserId;
        const isFullAdmin = this.$rootScope.userState.isFullAdmin;

        // Check if the user is an admin in the group or part of an admin group
        const isAdmin = this.admins.some(admin => {
            const isUserAdmin = admin.idUser === this.localUserId;
            const isUserInAdminGroup =
                admin.idUserGroup &&
                admin.users.some(adminFromGroup => adminFromGroup.idUser === this.localUserId);
            return isUserAdmin || isUserInAdminGroup;
        });

        return isOwner || isFullAdmin || isAdmin;
    }

    linkSitesPopup() {
        this.VrmModalService.openVueModal(InstallationGroupLinkInstallationsModal, {
            group: this.group,
        });
    }

    canUnlinkSite() {
        return this.canMakeChanges;
    }

    async unlinkSite(idSite) {
        const props = {
            message: this.$t('groups.confirm_deletion_message_installation'),
            action: this.$t('groups.confirm_remove_installation_action'),
        };

        const confirmed = await this.VrmModalService.openVueModal(ConfirmDeletionModal, props);

        if (confirmed) {
            this.InstallationGroupsService.patchInstallationGroup(this.group.idSiteGroup, {
                sites: [
                    {
                        idSite,
                        linked: false,
                    },
                ],
            });
        }
    }

    async removeInvite(email) {
        const props = {
            item: this.$t('users.confirm_deletion_item_pending_invitation'),
            message: this.$t('users.confirm_deletion_message_pending_invitation'),
            action: this.$t('users.confirm_remove_invite_action'),
        };

        const confirmed = await this.VrmModalService.openVueModal(ConfirmDeletionModal, props);

        if (confirmed) {
            await this.InstallationGroupsService.removeInvite(this.group.idSiteGroup, email);
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_SUCCESS,
                message: this.$t('users.invite_removed_message'),
            });
        }
    }

    get combinedUserList() {
        const self = this;
        return [
            ...this.group.users.map(user => ({
                key: `u${user.idUser}`,
                title: user.name,
                isGroup: false,
                user,
                get accessLevelOption() {
                    return findAccessLevelOption(user.accessLevel);
                },
                set accessLevelOption(option) {
                    self.updateAccessLevel(user, option.accessLevel, 'users', 'idUser');
                },
            })),
            ...this.group.userGroups.map(group => ({
                key: `g${group.idUserGroup}`,
                title: group.name,
                isGroup: true,
                group,
                get accessLevelOption() {
                    return findAccessLevelOption(group.accessLevel);
                },
                set accessLevelOption(option) {
                    self.updateAccessLevel(group, option.accessLevel, 'userGroups', 'idUserGroup');
                },
            })),
        ].sort((a, b) => {
            return a.title.localeCompare(b.title);
        });
    }

    /**
     * Update access level of user or user group
     * @param {Object} item - user or user group
     * @param {number} accessLevel - new access level
     * @param {string} key - 'users' or 'userGroups'
     * @param {string} idKey - 'idUser' or 'idUserGroup'
     */
    updateAccessLevel(item, accessLevel, key, idKey) {
        const originalAccessLevel = item.accessLevel;
        item.accessLevel = accessLevel;
        this.InstallationGroupsService.patchInstallationGroup(this.group.idSiteGroup, {
            [key]: [
                {
                    [idKey]: item[idKey],
                    accessLevel,
                },
            ],
        }).catch(() => {
            item.accessLevel = originalAccessLevel;
        });
    }

    async unlinkUser(idUser) {
        const props = {
            item: this.$t('users.confirm_deletion_item_user'),
            message: this.$t('users.confirm_deletion_message_user'),
            action: this.$t('users.confirm_unlink_action'),
        };

        const confirmed = await this.VrmModalService.openVueModal(ConfirmDeletionModal, props);

        if (confirmed) {
            this.InstallationGroupsService.patchInstallationGroup(this.group.idSiteGroup, {
                users: [
                    {
                        idUser,
                        accessLevel: null,
                    },
                ],
            });
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_SUCCESS,
                message: this.$t('users.user_removed_message'),
            });
        }
    }

    async unlinkUserGroup(idUserGroup) {
        if (this.busy) return;
        this.busy = true;
        const props = {
            item: this.$t('users.confirm_deletion_item_team'),
            message: this.$t('users.confirm_deletion_message_team'),
            action: this.$t('users.confirm_unlink_action'),
        };

        const confirmed = await this.VrmModalService.openVueModal(ConfirmDeletionModal, props);

        if (confirmed) {
            this.InstallationGroupsService.patchInstallationGroup(this.group.idSiteGroup, {
                userGroups: [
                    {
                        idUserGroup,
                        accessLevel: null,
                    },
                ],
            });
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_SUCCESS,
                message: this.$t('users.team_unlinked_message'),
            });
        }
        this.busy = false;
    }

    linkUsersPopup() {
        this.VrmModalService.openVueModal(InstallationGroupLinkUsersModal, {
            group: this.group,
        });
    }

    linkUserGroupsPopup() {
        this.VrmModalService.openVueModal(InstallationGroupLinkUserGroupsModal, {
            group: this.group,
        });
    }

    canModifyControl(item) {
        return item.isGroup
            ? this.canModifyUserGroupControl(item.group)
            : this.canModifyUserControl(item.user);
    }

    canModifyUserControl(user) {
        return this.canMakeChanges && user.idUser !== this.group.idOwner;
    }

    canModifyUserGroupControl() {
        return this.canMakeChanges;
    }

    itemIsOwner(item) {
        return !item.isGroup && item.user.idUser === this.group.idOwner;
    }

    showRoleGuideModal() {
        this.VrmModalService.openVueModal(UserRolesGuideModal);
    }
}
</script>
